import React from 'react';
import { AnchorButton, Button, Icon, Popover, Position, Tooltip } from '@blueprintjs/core';

// Shared list layout for the index screens (Jobs, Clients, Invoices, ...).
//
// A table describes itself with a column config:
//
//   { area: 'client', label: 'Client', width: 'minmax(180px, 1.4fr)',
//     bone: 'lines' | 'person' | 'pill' | 'short' | 'none', icon: 'map-marker' }
//
// and a card layout for narrow widths:
//
//   { columns: 'minmax(0, 1fr) auto', areas: ['name status', 'contact contact'],
//     footer: ['created', 'balance'] }
//
// The same config drives the table grid, the headings, the loading skeleton
// and the cards, so they can't drift apart. Lists switch to cards when the
// list itself is narrower than `minTableWidth` (measured, not a media query),
// so a 4-column list stays a table on a laptop where a 9-column one can't.
// People can hide and reorder columns from the list's Columns menu; the first
// column (the record link) and `actions` stay put, as does any column with
// `locked: true`.
// Styles: styles/data-list.scss.

const ListContext = React.createContext({ columns: {}, footer: [], hidden: [] });

const TWO_UP_WIDTH = 600;
const PHONE_WIDTH = 560;

const quoteAreas = (areas) => areas.map((row) => `"${row}"`).join(' ');

const layoutStyle = (columns, cards) => {
  const style = {
    '--dl-cols': columns.map((column) => column.width || 'minmax(0, 1fr)').join(' '),
    '--dl-areas': `"${columns.map((column) => column.area).join(' ')}"`,
  };
  if (cards) {
    style['--dl-card-cols'] = cards.columns || 'minmax(0, 1fr)';
    style['--dl-card-areas'] = quoteAreas(cards.areas);
  }
  return style;
};

// The narrowest the table can get before cells overflow their row: each
// column's minimum (a px width, or the px floor of a minmax()), plus the row's
// gaps and padding (see .dl-row). Used when minTableWidth is set lower.
const ROW_GAP = 14;
const ROW_PADDING = 36;

const naturalWidth = (columns) =>
  columns.reduce((sum, { width = '' }) => {
    const px = /^(?:minmax\()?\s*(\d+)px/.exec(width);
    return sum + (px ? parseInt(px[1], 10) : 0);
  }, ROW_GAP * (columns.length - 1) + ROW_PADDING);

// Column choices are kept per list in this browser, keyed by the noun and the
// list's set of columns, so two lists sharing a noun (the Invoices screen and
// a client's invoices tab) keep their own.
const COLUMNS_KEY = (noun, columns) =>
  `dl-columns:${noun}:${columns.map((column) => column.area).sort().join(',')}`;

const storedColumns = (key) => {
  try {
    const prefs = JSON.parse(window.localStorage.getItem(key));
    return prefs && Array.isArray(prefs.order) && Array.isArray(prefs.hidden) ? prefs : null;
  } catch (e) {
    return null;
  }
};

const storeColumns = (key, prefs) => {
  try {
    if (prefs) window.localStorage.setItem(key, JSON.stringify(prefs));
    else window.localStorage.removeItem(key);
  } catch (e) {
    // Private mode or blocked storage: the choice lasts until the page reloads.
  }
};

const isLocked = (column, index) => index === 0 || column.area === 'actions' || column.locked;

// The columns in the person's order, split into the ones they can move and
// hide and the locked ones at either end.
const arrangeColumns = (columns, prefs) => {
  const lead = columns.filter((column, index) => index === 0 || (isLocked(column, index) && column.area !== 'actions'));
  const tail = columns.filter((column) => column.area === 'actions');
  let movable = columns.filter((column, index) => !isLocked(column, index));
  if (prefs) {
    const rank = (area) => {
      const index = prefs.order.indexOf(area);
      return index === -1 ? prefs.order.length + columns.findIndex((column) => column.area === area) : index;
    };
    movable = [...movable].sort((a, b) => rank(a.area) - rank(b.area));
  }
  const hidden = prefs ? prefs.hidden.filter((area) => movable.some((column) => column.area === area)) : [];
  return { lead, movable, tail, hidden };
};

const useColumns = (noun, columns) => {
  const key = COLUMNS_KEY(noun, columns);
  const [prefs, setPrefs] = React.useState(() => storedColumns(key));
  const { lead, movable, tail, hidden } = arrangeColumns(columns, prefs);
  const visible = [...lead, ...movable.filter((column) => !hidden.includes(column.area)), ...tail];
  const update = (next) => {
    storeColumns(key, next);
    setPrefs(next);
  };
  return { visible, movable, hidden, customised: Boolean(prefs), update };
};

// Hiding columns makes the table narrower, so it needs less room before it
// scrolls: scale the list's minTableWidth by the share of width still shown.
const tableWidth = (minTableWidth, all, visible) => {
  const shown = naturalWidth(visible);
  const scaled = (minTableWidth || 0) * (shown / naturalWidth(all));
  return Math.max(Math.round(scaled), shown);
};

const columnLabel = (column) =>
  column.label || column.area.replace(/_/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase());

const ColumnsMenu = ({ movable, hidden, customised, onChange }) => {
  const order = movable.map((column) => column.area);
  const save = (nextOrder, nextHidden) => onChange({ order: nextOrder, hidden: nextHidden });
  const toggle = (area) =>
    save(order, hidden.includes(area) ? hidden.filter((item) => item !== area) : [...hidden, area]);
  const move = (index, step) => {
    const next = [...order];
    [next[index], next[index + step]] = [next[index + step], next[index]];
    save(next, hidden);
  };
  return (
    <Popover position={Position.BOTTOM_RIGHT} minimal={true}>
      <button type="button" className="dl-columns-button" title="Choose columns">
        <Icon icon="column-layout" iconSize={14} />
        <span>Columns</span>
      </button>
      <div className="option-picker dl-columns">
        <div className="dl-columns__title">Show and order columns</div>
        <ul className="option-picker__list dl-columns__list">
          {movable.map((column, index) => {
            const shown = !hidden.includes(column.area);
            const label = columnLabel(column);
            return (
              <li key={column.area} className="option-picker__option dl-columns__item">
                <button
                  type="button"
                  className="dl-columns__toggle"
                  role="checkbox"
                  aria-checked={shown}
                  onClick={() => toggle(column.area)}
                >
                  <span className={`option-picker__check${shown ? ' is-checked' : ''}`}>
                    {shown && <Icon icon="small-tick" iconSize={14} />}
                  </span>
                  <span className="option-picker__label">{label}</span>
                </button>
                <button
                  type="button"
                  className="dl-columns__move"
                  aria-label={`Move ${label} up`}
                  title="Move up"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <Icon icon="chevron-up" iconSize={14} />
                </button>
                <button
                  type="button"
                  className="dl-columns__move"
                  aria-label={`Move ${label} down`}
                  title="Move down"
                  disabled={index === movable.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <Icon icon="chevron-down" iconSize={14} />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="option-picker__footer">
          <span>{movable.length - hidden.length} of {movable.length} shown</span>
          <button type="button" className="dl-columns__reset" disabled={!customised} onClick={() => onChange(null)}>
            Reset
          </button>
        </div>
      </div>
    </Popover>
  );
};

const pluralise = (count, noun) => {
  if (count !== 1) return noun;
  return /ies$/.test(noun) ? noun.replace(/ies$/, 'y') : noun.replace(/s$/, '');
};

// Lists start as a table everywhere; where it gets narrow (it scrolls
// sideways) people can switch to cards. The choice is remembered per noun in
// this browser; `defaultView="cards"` makes cards the starting point.
const VIEW_KEY = (noun) => `dl-view:${noun}`;

const storedView = (noun) => {
  try {
    const view = window.localStorage.getItem(VIEW_KEY(noun));
    return view === 'table' || view === 'cards' ? view : null;
  } catch (e) {
    return null;
  }
};

const storeView = (noun, view) => {
  try {
    window.localStorage.setItem(VIEW_KEY(noun), view);
  } catch (e) {
    // Private mode or blocked storage: the choice lasts until the page reloads.
  }
};

const ViewSwitch = ({ view, onChange }) => (
  <div className="dl-view-switch" role="group" aria-label="View">
    {[
      { value: 'table', icon: 'th', label: 'Table' },
      { value: 'cards', icon: 'list-detail-view', label: 'Cards' },
    ].map((option) => (
      <button
        key={option.value}
        type="button"
        className={`dl-view-switch__option${view === option.value ? ' is-active' : ''}`}
        aria-pressed={view === option.value}
        title={`${option.label} view`}
        onClick={() => onChange(option.value)}
      >
        <Icon icon={option.icon} iconSize={14} />
        <span>{option.label}</span>
      </button>
    ))}
  </div>
);

// Tracks the list's own width and picks table, two-up cards or one-up cards.
// Children may be a function of { narrow, view, setView } to show the switch.
class Measured extends React.Component {
  state = {
    mode: 'table',
    phone: false,
    view: (this.props.noun && storedView(this.props.noun)) || this.props.defaultView || 'table',
  };

  ref = React.createRef();

  componentDidMount() {
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.ref.current);
    } else {
      window.addEventListener('resize', this.measure);
    }
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.measure);
  }

  measure = () => {
    const node = this.ref.current;
    if (!node) return;
    const width = node.getBoundingClientRect().width;
    const { minTableWidth = 0, cards, preferTable } = this.props;
    let mode = 'table';
    if (preferTable) {
      // Stay a table (scrolling sideways when tight); cards only on phones.
      if (cards && width < PHONE_WIDTH) mode = 'cards-1';
    } else if (cards && width < minTableWidth) {
      mode = width >= TWO_UP_WIDTH ? 'cards-2' : 'cards-1';
    }
    const phone = width < PHONE_WIDTH;
    if (mode !== this.state.mode || phone !== this.state.phone) this.setState({ mode, phone });
  };

  setView = (view) => {
    if (this.props.noun) storeView(this.props.noun, view);
    this.setState({ view });
  };

  render() {
    const { className = '', style, children, ...rest } = this.props;
    const { mode: measured, phone, view } = this.state;
    const narrow = measured !== 'table';
    const mode = narrow && view === 'table' ? 'table' : measured;
    const scroll = mode === 'table' && (rest.preferTable || narrow) ? ' dl--scroll' : '';
    const modeClass = mode === 'table' ? `dl--table${scroll}` : `dl--cards dl--${mode}`;
    const scrollStyle = scroll ? { ...style, '--dl-min-width': `${rest.minTableWidth || 0}px` } : style;
    delete rest.minTableWidth;
    delete rest.cards;
    delete rest.preferTable;
    delete rest.defaultView;
    delete rest.noun;
    return (
      <div ref={this.ref} className={`dl ${modeClass}${phone ? ' dl--phone' : ''} ${className}`} style={scrollStyle} {...rest}>
        {typeof children === 'function' ? children({ narrow, view, setView: this.setView }) : children}
      </div>
    );
  }
}

const Head = ({ columns }) => (
  <div className="dl-row dl-row--head" role="row">
    {columns.map(({ area, label }) => (
      <div key={area} className="dl-cell" style={{ gridArea: area }} role="columnheader">
        {label}
      </div>
    ))}
  </div>
);

// `total` and `noun` make the "46 jobs" line; `note` sits opposite it (e.g.
// the sort order) and is hidden on phones. `preferTable` keeps the table
// (scrolling sideways below minTableWidth) and uses cards only on phones.
// Where the list is narrow, a Table/Cards switch appears; `defaultView`
// picks which one a first-time viewer gets (table unless told otherwise).
export const List = ({ columns, cards, minTableWidth, preferTable, defaultView, total, noun, note, className = '', children }) => {
  const { visible, movable, hidden, customised, update } = useColumns(noun, columns);
  const context = {
    columns: columns.reduce((map, column) => ({ ...map, [column.area]: column }), {}),
    footer: (cards && cards.footer) || [],
    hidden,
  };
  return (
    <ListContext.Provider value={context}>
      <Measured
        className={className}
        style={layoutStyle(visible, cards)}
        minTableWidth={tableWidth(minTableWidth, columns, visible)}
        preferTable={preferTable}
        defaultView={defaultView}
        noun={noun}
        cards={cards}
        role="table"
        aria-label={noun}
      >
        {({ narrow, view, setView }) => (
          <React.Fragment>
            <div className="dl-summary">
              <span className="dl-summary__count">
                {total} {pluralise(total, noun)}
              </span>
              {note && <span className="dl-summary__note">{note}</span>}
              <div className="dl-summary__tools">
                {movable.length > 0 && (
                  <ColumnsMenu movable={movable} hidden={hidden} customised={customised} onChange={update} />
                )}
                {narrow && <ViewSwitch view={view} onChange={setView} />}
              </div>
            </div>
            <Head columns={visible} />
            {children}
          </React.Fragment>
        )}
      </Measured>
    </ListContext.Provider>
  );
};

// `accent` draws a slim coloured bar on the left edge (e.g. service type).
export const Row = ({ accent, className = '', children }) => (
  <div
    className={`dl-row${accent ? ' dl-row--accent' : ''} ${className}`}
    role="row"
    style={accent ? { '--dl-accent': accent } : undefined}
  >
    {children}
  </div>
);

// Cells are placed by area. A column's `icon` labels the cell on cards, where
// there are no headings; footer areas sit under a divider on cards.
export const Cell = ({ area, className = '', children }) => (
  <ListContext.Consumer>
    {({ columns, footer, hidden = [] }) => {
      if (hidden.includes(area)) return null;
      const icon = columns[area] && columns[area].icon;
      const classes = [
        'dl-cell',
        `dl-cell--${area}`,
        icon && 'dl-cell--icon',
        footer.includes(area) && 'dl-cell--footer',
        className,
      ].filter(Boolean).join(' ');
      return (
        <div className={classes} style={{ gridArea: area }} role="cell">
          {icon && <Icon className="dl-cell__icon" icon={icon} iconSize={14} />}
          {icon ? <div className="dl-cell__body">{children}</div> : children}
        </div>
      );
    }}
  </ListContext.Consumer>
);

// The record link. It stretches over the whole row, so anywhere on the row
// opens the record; other links and buttons in the row use `Raised`.
export const RowLink = ({ href, label, children }) => (
  <a className="dl-link" href={href} aria-label={label}>
    {children}
  </a>
);

export const Raised = ({ as: Tag = 'span', className = '', children, ...rest }) => (
  <Tag className={`dl-raised ${className}`} {...rest}>
    {children}
  </Tag>
);

// The standard cell: a primary line over a muted line. `href` makes the
// primary line a (raised) link to something other than the row's record.
export const Lines = ({ primary, secondary, href, strong = true, title }) => (
  <div className="dl-lines">
    {href ? (
      <a className="dl-lines__primary dl-lines__primary--link dl-raised" href={href} title={title || (typeof primary === 'string' ? primary : undefined)}>
        {primary}
      </a>
    ) : (
      <div
        className={`dl-lines__primary${strong ? '' : ' dl-lines__primary--plain'}`}
        title={title || (typeof primary === 'string' ? primary : undefined)}
      >
        {primary}
      </div>
    )}
    {secondary && (
      <div className="dl-muted dl-truncate" title={typeof secondary === 'string' ? secondary : undefined}>
        {secondary}
      </div>
    )}
  </div>
);

export const Muted = ({ children }) => <span className="dl-muted">{children}</span>;

// Text shown only on cards, e.g. the "Created " before a date.
export const CardOnly = ({ children }) => <span className="dl-card-only">{children}</span>;

// tone: green | grey | blue | amber | red | purple
export const Pill = ({ tone = 'grey', icon, children, style }) => (
  <span className={`dl-pill dl-pill--${tone}`} style={style}>
    {icon && <Icon icon={icon} iconSize={12} />}
    {children}
  </span>
);

export const initials = (name = '') =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

// Initials avatar with a name and a muted line; `dot` adds a status dot.
export const Person = ({ name, secondary, dot, empty }) => (
  <div className={`dl-person${empty ? ' dl-person--empty' : ''}`}>
    <span className="dl-person__avatar" aria-hidden="true">
      {!empty && initials(name)}
      {dot && <span className="dl-person__dot" style={{ backgroundColor: dot }} />}
    </span>
    <span className="dl-person__text">
      {!empty && <span className="dl-person__name" title={name}>{name}</span>}
      {secondary && <span className="dl-muted dl-truncate">{secondary}</span>}
    </span>
  </div>
);

// Row actions: small icon buttons, quiet until the row is hovered.
// items: [{ icon, label, onClick, href, intent, disabled }]
export const Actions = ({ items }) => (
  <div className="dl-actions dl-raised">
    {items.filter(Boolean).map((item) => (
      <Tooltip key={item.label} content={item.label} position={Position.TOP}>
        {item.href ? (
          <AnchorButton
            minimal={true}
            small={true}
            icon={item.icon}
            aria-label={item.label}
            href={item.href}
            target={item.target}
            intent={item.intent}
            disabled={item.disabled}
          />
        ) : (
          <Button
            minimal={true}
            small={true}
            icon={item.icon}
            aria-label={item.label}
            onClick={item.onClick}
            intent={item.intent}
            disabled={item.disabled}
          />
        )}
      </Tooltip>
    ))}
  </div>
);

// Empty and error states share this layout.
// actions: [{ label, icon, onClick, primary, disabled }]
export const Notice = ({ icon, tone, title, message, actions = [], role }) => (
  <div className={`dl-notice${tone ? ` dl-notice--${tone}` : ''}`} role={role}>
    <span className="dl-notice__icon" aria-hidden="true">
      <Icon icon={icon} iconSize={22} />
    </span>
    <h4>{title}</h4>
    {message && <p>{message}</p>}
    {actions.filter(Boolean).length > 0 && (
      <div className="dl-notice__actions">
        {actions.filter(Boolean).map((action) => (
          <Button
            key={action.label}
            intent={action.primary ? 'primary' : 'none'}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}
      </div>
    )}
  </div>
);

const FILTER_LIST = (names) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== '' && value !== false;
};

// "Newest first by date created" etc., from a search's `order`
// ({ created_at: 'desc' }). Unknown fields give no note.
const SORT_FIELDS = {
  created_at: { label: 'date created', date: true },
  updated_at: { label: 'last updated', date: true },
  start_date: { label: 'start date', date: true },
  end_date: { label: 'end date', date: true },
  due_date: { label: 'due date', date: true },
  issued_date: { label: 'issue date', date: true },
  name: { label: 'name' },
  first_name: { label: 'first name' },
  last_name: { label: 'last name' },
  title: { label: 'title' },
};

export const sortNote = (order) => {
  const key = order && Object.keys(order)[0];
  const field = key && SORT_FIELDS[key];
  if (!field) return null;
  const desc = String(order[key]).toLowerCase() === 'desc';
  if (field.date) return `${desc ? 'Newest' : 'Oldest'} first by ${field.label}`;
  return `${desc ? 'Z to A' : 'A to Z'} by ${field.label}`;
};

// Names of the filters set in `where`, given { key: 'readable name' }.
export const activeFilters = (where, names) =>
  Array.from(new Set(
    Object.keys(names).filter((key) => hasValue((where || {})[key])).map((key) => names[key])
  ));

// Empty list. `filters` are readable names of the filters in use; pass
// onClearFilters only where the screen can actually reset them.
export const Empty = ({ noun, filters = [], term, onClearFilters, title, message, extraAction }) => {
  const filtered = filters.length > 0 || Boolean(term);
  let text = message;
  if (!text) {
    if (term && filters.length > 0) text = `Searching for “${term}” and filtering by ${FILTER_LIST(filters)}.`;
    else if (term) text = `Nothing matches “${term}”. Check the spelling or try fewer words.`;
    else if (filters.length > 0) text = `Filtering by ${FILTER_LIST(filters)}.`;
    else text = `There are no ${noun} yet.`;
  }
  return (
    <Notice
      icon={filtered ? 'filter-remove' : 'inbox'}
      title={title || (filtered ? `No matching ${noun}` : `No ${noun} yet`)}
      message={text}
      actions={[
        filtered && onClearFilters && {
          label: term && filters.length === 0 ? 'Clear search' : 'Clear filters',
          icon: 'filter-remove',
          onClick: onClearFilters,
          primary: true,
        },
        extraAction,
      ]}
    />
  );
};

// Failed load. Retries by itself when the browser comes back online.
// `slowHint` = { message, label, onClick } offers a narrower search.
export class LoadError extends React.Component {
  state = { online: typeof navigator === 'undefined' || navigator.onLine !== false };

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.setState({ online: true });
    if (this.props.onRetry) this.props.onRetry();
  };

  handleOffline = () => this.setState({ online: false });

  render() {
    // `noFilters`: a list without search or filters (e.g. a settings list), so
    // there's no search to keep.
    const { noun, onRetry, slowHint, noFilters } = this.props;
    const { online } = this.state;
    const hint = online && slowHint;

    let message = noFilters
      ? 'The server didn\'t respond. Try again in a moment.'
      : 'The server didn\'t respond. Your filters are kept, so trying again runs the same search.';
    if (!online) message = 'You\'re offline. The list will reload as soon as the connection is back.';
    else if (hint) message = hint.message;

    return (
      <Notice
        role="alert"
        tone="error"
        icon={online ? 'error' : 'offline'}
        title={`Couldn't load ${noun}`}
        message={message}
        actions={[
          hint && { label: hint.label, icon: 'calendar', onClick: hint.onClick, primary: true },
          onRetry && { label: 'Try again', icon: 'refresh', onClick: onRetry, primary: !hint, disabled: !online },
        ]}
      />
    );
  }
}

// Loading skeleton built from the same column config, so headings and
// layout match the loaded list and nothing jumps when rows arrive.
const BONE_WIDTHS = [
  [62, 48, 72, 38, 58, 66],
  [78, 60, 54, 46, 70, 52],
  [54, 72, 66, 30, 48, 74],
  [70, 40, 80, 52, 62, 44],
];

const Bone = ({ width, height = 12, round }) => (
  <span className={`dl-bone${round ? ' dl-bone--round' : ''}`} style={{ width, height }} />
);

const BoneLines = ({ first, second }) => (
  <div className="dl-bone-lines">
    <Bone width={first} height={13} />
    <Bone width={second} height={10} />
  </div>
);

const boneFor = (kind, w, index) => {
  switch (kind) {
    case 'none':
      return null;
    case 'pill':
      return <Bone width={index % 2 ? 72 : 86} height={24} round={true} />;
    case 'short':
      return <BoneLines first={48 + (w[2] % 30)} second={`${w[2]}%`} />;
    case 'single':
      return <Bone width={`${w[1] + 10}%`} height={13} />;
    case 'person':
      return (
        <div className="dl-bone-person">
          <Bone width={28} height={28} round={true} />
          <BoneLines first={`${w[4]}%`} second={`${w[3] + 10}%`} />
        </div>
      );
    default:
      return <BoneLines first={`${w[5]}%`} second={`${w[1] + 15}%`} />;
  }
};

export const Loading = ({ columns: allColumns, cards, minTableWidth, preferTable, defaultView, noun, rows = 8 }) => {
  // The person's chosen columns, so the skeleton matches the loaded list.
  const { visible: columns } = useColumns(noun, allColumns);
  return (
    <ListContext.Provider value={{ columns: {}, footer: (cards && cards.footer) || [] }}>
      <Measured
        className="dl--loading"
        style={layoutStyle(columns, cards)}
        minTableWidth={tableWidth(minTableWidth, allColumns, columns)}
        preferTable={preferTable}
        defaultView={defaultView}
        noun={noun}
        cards={cards}
        role="status"
        aria-label={`Loading ${noun}`}
      >
        <div className="dl-summary">
          <Bone width={84} height={14} />
        </div>
        <Head columns={columns} />
        {Array.from({ length: rows }, (_, index) => {
          const w = BONE_WIDTHS[index % BONE_WIDTHS.length];
          return (
            <div className="dl-row" key={index} aria-hidden="true">
              {columns.map((column) => (
                <div
                  key={column.area}
                  className={`dl-cell${cards && (cards.footer || []).includes(column.area) ? ' dl-cell--footer' : ''}`}
                  style={{ gridArea: column.area }}
                >
                  {boneFor(column.bone, w, index)}
                </div>
              ))}
            </div>
          );
        })}
      </Measured>
    </ListContext.Provider>
  );
};
